import type { LegacyRef } from 'react';
import { forwardRef } from 'react';

import { WidgetProps } from '@highimpact-front/remote-widget-core';
import {
  ApiDashboardElement,
  ApiReturnCampaign,
} from '@highimpact-frontend/api-types';
import {
  DisplayerTypeEnum,
  isDisplayerTypeSuccess,
} from '@highimpact-frontend/displayer-types';
import {
  DisplayerErrorMessage,
  GenericErrorBoundary,
} from '@highimpact/dashboard-ui';

import { useDashboardLibrarySelector } from '../../../../runtime/DashboardLibraryProvider';
import { buildElementNavDisplayer } from '../../../../runtime/buildElementNavDisplayer';
import { findGridIdAndTypeByElementId } from '../../../../state/store-operations';
import Widget from '../../commons/Widget';
import HistogramPlot from '../histogramPlot/HistogramPlot';
import OmbrothermicPlot from '../ombrothermicPlot/OmbrothermicPlot';
import ScatterPlot from '../scatterPlot/ScatterPlot';
import WindRosePlot from '../windRosePlot/WindRosePlot';
import DisplayerNavComponent from './DisplayerNavComponent';
import { resolveRendererTypeFromElement } from './displayerElementConsistency';

interface ElementMeasureDisplayerProps extends WidgetProps {
  displayer_id: number;
  elementData: ApiDashboardElement;
  dashboardElementId: number;
  gridId: number;
  campaigns: ApiReturnCampaign[];
}

const nav_props = {
  [DisplayerTypeEnum.timeseries]: {
    download: true,
    zoom: false,
    edit: true,
    delete: true,
    title: true,
  },
  [DisplayerTypeEnum.scatter]: {
    download: true,
    zoom: false,
    edit: true,
    delete: true,
    title: true,
  },
  [DisplayerTypeEnum.rose_wind]: {
    download: true,
    edit: true,
    delete: true,
    title: true,
  },
  [DisplayerTypeEnum.histogram]: {
    download: true,
    zoom: false,
    edit: true,
    delete: true,
    title: true,
  },
  [DisplayerTypeEnum.ombrothermic]: {
    download: true,
    zoom: false,
    edit: true,
    delete: true,
    title: true,
  },
  DisplayerTypeError: { delete: true, title: true },
};

function ElementMeasureDisplayerWidget(
  {
    displayer_id,
    is_editable,
    dashboardOnlyMode,
    non_removable,
    elementData,
    widget_id,
    gridId,
    site,
    campaigns,
    ...props
  }: ElementMeasureDisplayerProps,
  ref: LegacyRef<HTMLDivElement>
) {
  const { dashboardElementId } = props;
  const navDisplayer = buildElementNavDisplayer(elementData, displayer_id);
  const rendererType = resolveRendererTypeFromElement(elementData);

  const gridData = useDashboardLibrarySelector((state) =>
    findGridIdAndTypeByElementId(dashboardElementId, state.dashboard)
  );

  const plotSubProps = {
    dashboardElementId,
    is_editable,
    elementData,
    non_removable,
    gridId,
    widget_id,
    site,
    campaigns,
  };

  const plotContent = (() => {
    if (rendererType === 'DisplayerTypeError') {
      return <DisplayerErrorMessage name={elementData.name} />;
    }

    return (
      <GenericErrorBoundary
        fallback={<DisplayerErrorMessage name={"Impossible d'afficher"} />}
      >
        {
          {
            [DisplayerTypeEnum.timeseries]: <ScatterPlot {...plotSubProps} />,
            [DisplayerTypeEnum.scatter]: <ScatterPlot {...plotSubProps} />,
            [DisplayerTypeEnum.rose_wind]: <WindRosePlot {...plotSubProps} />,
            [DisplayerTypeEnum.histogram]: <HistogramPlot {...plotSubProps} />,
            [DisplayerTypeEnum.ombrothermic]: (
              <OmbrothermicPlot {...plotSubProps} />
            ),
          }[
            rendererType as
              | DisplayerTypeEnum.timeseries
              | DisplayerTypeEnum.scatter
              | DisplayerTypeEnum.rose_wind
              | DisplayerTypeEnum.histogram
              | DisplayerTypeEnum.ombrothermic
          ]
        }
      </GenericErrorBoundary>
    );
  })();

  const gridType = gridData?.type;

  return (
    <Widget
      ref={ref}
      is_editable={is_editable}
      non_removable={non_removable}
      widget_id={widget_id}
      dashboardOnlyMode={dashboardOnlyMode}
      site={site}
      {...props}
      className={`displayer ${props.className || ''}`}
    >
      {isDisplayerTypeSuccess(navDisplayer) && (
        <DisplayerNavComponent
          {...plotSubProps}
          displayer={navDisplayer}
          dashboardOnlyMode={dashboardOnlyMode}
          buttons={
            dashboardOnlyMode
              ? {}
              : {
                  ...(nav_props[rendererType as keyof typeof nav_props] ?? {}),
                  delete: !non_removable,
                }
          }
          gridId={gridId}
          gridType={gridType!}
        />
      )}

      {plotContent}
    </Widget>
  );
}

export default forwardRef(ElementMeasureDisplayerWidget);
